import { useEffect, useState } from "react";
import { Gift, Plus, PencilSimple, Trash, PaperPlaneTilt, MagnifyingGlass, CheckCircle, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const EMPTY = { name: "", description: "", value: "", scadenza_mode: "date", valid_from: "", valid_to: "", giorni_validita: 365, active: true };
const EMPTY_CUST = { first_name: "", last_name: "", phone: "", email: "" };

const fmtEur = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("it-IT"); } catch { return iso; }
};

const STATUS_BADGE = {
  attiva: "bg-emerald-100 text-emerald-700",
  utilizzata: "bg-zinc-200 text-zinc-600",
  scaduta: "bg-red-100 text-red-700",
};

export default function GiftCards() {
  const { user } = useAuth();
  const isSala = user?.role === "sala";
  const [tab, setTab] = useState(isSala ? "cerca" : "cards");
  const [cards, setCards] = useState([]);
  const [instances, setInstances] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [editor, setEditor] = useState(null); // null | {id?} con form
  const [form, setForm] = useState(EMPTY);
  const [assignCard, setAssignCard] = useState(null);
  const [custSel, setCustSel] = useState("");
  const [custForm, setCustForm] = useState(EMPTY_CUST);
  const [sendEmail, setSendEmail] = useState(true);
  const [lookupCode, setLookupCode] = useState("");
  const [lookupRes, setLookupRes] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (isSala) return; // la sala usa solo la ricerca per codice
    try {
      const [c, i, cu] = await Promise.all([
        api.get("/gift-cards"), api.get("/gift-card-instances"), api.get("/customers")]);
      setCards(c.data); setInstances(i.data); setCustomers(cu.data);
    } catch { toast.error("Errore caricamento gift card"); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditor({}); };
  const openEdit = (c) => {
    setForm({ name: c.name, description: c.description || "", value: c.value, scadenza_mode: c.scadenza_mode || "date",
              valid_from: c.valid_from || "", valid_to: c.valid_to || "", giorni_validita: c.giorni_validita || 365,
              active: c.active });
    setEditor({ id: c.id });
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nome obbligatorio");
    if (!(Number(form.value) > 0)) return toast.error("Inserisci un valore maggiore di zero");
    if (form.scadenza_mode === "date" && (!form.valid_from || !form.valid_to))
      return toast.error("Inserisci le date di validità");
    setSaving(true);
    try {
      const payload = { ...form, value: Number(form.value), giorni_validita: Number(form.giorni_validita) || 365 };
      if (editor.id) await api.put(`/gift-cards/${editor.id}`, payload);
      else await api.post("/gift-cards", payload);
      toast.success(editor.id ? "Gift card aggiornata" : "Gift card creata");
      setEditor(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const del = async (c) => {
    if (!window.confirm(`Eliminare la gift card "${c.name}"? Verranno eliminate anche le assegnazioni.`)) return;
    try { await api.delete(`/gift-cards/${c.id}`); toast.success("Gift card eliminata"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore eliminazione"); }
  };

  const openAssign = (c) => { setAssignCard(c); setCustSel(""); setCustForm(EMPTY_CUST); setSendEmail(true); };
  const onPickCustomer = (id) => {
    setCustSel(id);
    const c = customers.find((x) => x.id === id);
    if (c) setCustForm({ first_name: c.first_name || "", last_name: c.last_name || "", phone: c.phone || "", email: c.email || "" });
    else setCustForm(EMPTY_CUST);
  };
  const doAssign = async () => {
    if (!custForm.phone.trim() && !custForm.email.trim())
      return toast.error("Inserisci almeno telefono o email del cliente");
    setSaving(true);
    try {
      const r = await api.post(`/gift-cards/${assignCard.id}/assign`, { customer: custForm, send_email: sendEmail });
      toast.success(`Gift card assegnata — codice ${r.data.code}${r.data.notify?.status === "inviato" ? ", email inviata" : ""}`);
      setAssignCard(null); setTab("assegnate"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore assegnazione"); }
    finally { setSaving(false); }
  };

  const doLookup = async () => {
    if (!lookupCode.trim()) return;
    try { const r = await api.get(`/gift-cards/lookup/${lookupCode.trim()}`); setLookupRes(r.data); }
    catch (e) { setLookupRes(null); toast.error(e.response?.data?.detail || "Gift card non trovata"); }
  };
  const doRedeem = async (code) => {
    try {
      await api.post("/gift-cards/redeem", { code });
      toast.success("Gift card segnata come utilizzata");
      setLookupRes(null); setLookupCode(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="space-y-5" data-testid="giftcards-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-display font-extrabold text-zinc-900 flex items-center gap-2">
          <Gift size={26} weight="bold" className="text-cyan-600" /> Gift Card
        </h1>
        {!isSala && (
          <button onClick={openNew} data-testid="gc-new-btn"
            className="flex items-center gap-2 bg-zinc-900 text-white text-sm font-semibold px-4 py-2.5 rounded-lg hover:bg-zinc-700 transition-colors">
            <Plus size={16} weight="bold" /> Nuova gift card
          </button>
        )}
      </div>

      {!isSala && (
        <div className="flex gap-1 border-b border-zinc-200">
          {[["cards", "Gift Card"], ["assegnate", `Assegnate (${instances.length})`], ["cerca", "Cerca codice"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`gc-tab-${k}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === k ? "border-cyan-500 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}>
              {l}
            </button>
          ))}
        </div>
      )}

      {tab === "cards" && (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="gc-list">
          {cards.length === 0 && (
            <div className="col-span-full bg-white border border-dashed border-zinc-300 rounded-xl p-8 text-center text-sm text-zinc-500">
              Nessuna gift card creata. Clicca "Nuova gift card" per iniziare.
            </div>
          )}
          {cards.map((c) => (
            <div key={c.id} className="bg-white border border-zinc-200 rounded-xl p-4 space-y-3" data-testid={`gc-card-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-bold text-zinc-900 truncate">{c.name}</div>
                  <div className="text-xs text-zinc-500">
                    {c.scadenza_mode === "giorni"
                      ? `Valida ${c.giorni_validita} giorni dall'assegnazione`
                      : `Valida dal ${fmtDate(c.valid_from)} al ${fmtDate(c.valid_to)}`}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-lg font-extrabold text-cyan-700">{fmtEur(c.value)}</div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${c.active ? "bg-emerald-100 text-emerald-700" : "bg-zinc-200 text-zinc-500"}`}>
                    {c.active ? "ATTIVA" : "DISATTIVA"}
                  </span>
                </div>
              </div>
              {c.description && <div className="text-xs text-zinc-600 line-clamp-2">{c.description}</div>}
              <div className="text-xs text-zinc-500">
                Assegnate: <b>{c.assegnate}</b> · Utilizzate: <b>{c.utilizzate}</b>
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={() => openAssign(c)} disabled={!c.active} data-testid={`gc-assign-${c.id}`}
                  className="flex items-center gap-1.5 text-xs font-semibold bg-cyan-600 text-white px-3 py-2 rounded-md hover:bg-cyan-500 disabled:opacity-40 transition-colors">
                  <PaperPlaneTilt size={14} weight="bold" /> Assegna
                </button>
                <button onClick={() => openEdit(c)} data-testid={`gc-edit-${c.id}`}
                  className="flex items-center gap-1.5 text-xs font-semibold border border-zinc-300 px-3 py-2 rounded-md hover:bg-zinc-50 transition-colors">
                  <PencilSimple size={14} weight="bold" /> Modifica
                </button>
                <button onClick={() => del(c)} data-testid={`gc-delete-${c.id}`}
                  className="flex items-center gap-1.5 text-xs font-semibold border border-red-200 text-red-600 px-3 py-2 rounded-md hover:bg-red-50 transition-colors">
                  <Trash size={14} weight="bold" /> Elimina
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "assegnate" && (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-x-auto" data-testid="gc-instances">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-xs text-zinc-500 border-b border-zinc-200">
                <th className="px-4 py-3">Codice</th><th className="px-4 py-3">Gift Card</th>
                <th className="px-4 py-3">Cliente</th><th className="px-4 py-3">Valore</th>
                <th className="px-4 py-3">Scadenza</th><th className="px-4 py-3">Stato</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {instances.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-500 text-sm">Nessuna gift card assegnata.</td></tr>
              )}
              {instances.map((i) => (
                <tr key={i.id} className="border-b border-zinc-100 last:border-0" data-testid={`gc-inst-${i.id}`}>
                  <td className="px-4 py-3 font-mono font-bold text-zinc-900">{i.code}</td>
                  <td className="px-4 py-3">{i.name || "—"}</td>
                  <td className="px-4 py-3">
                    <div>{i.customer_name}</div>
                    {i.customer_phone && <div className="text-xs text-zinc-500">{i.customer_phone}</div>}
                  </td>
                  <td className="px-4 py-3 font-semibold">{fmtEur(i.value)}</td>
                  <td className="px-4 py-3 text-xs text-zinc-600">{fmtDate(i.validita_fine)}</td>
                  <td className="px-4 py-3">
                    <span data-testid={`gc-status-${i.id}`} className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_BADGE[i.status]}`}>
                      {i.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {i.status === "attiva" && (
                      <button onClick={() => doRedeem(i.code)} data-testid={`gc-use-${i.id}`}
                        className="text-xs font-semibold text-emerald-700 border border-emerald-300 px-2.5 py-1.5 rounded-md hover:bg-emerald-50 transition-colors">
                        Segna utilizzata
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "cerca" && (
        <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4 max-w-xl" data-testid="gc-lookup">
          <div className="flex gap-2">
            <input value={lookupCode} onChange={(e) => setLookupCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && doLookup()}
              placeholder="GC-XXXXXX" data-testid="gc-lookup-input"
              className="flex-1 border border-zinc-300 rounded-lg px-3 py-2.5 text-sm font-mono uppercase" />
            <button onClick={doLookup} data-testid="gc-lookup-btn"
              className="flex items-center gap-2 bg-zinc-900 text-white text-sm font-semibold px-4 py-2.5 rounded-lg hover:bg-zinc-700 transition-colors">
              <MagnifyingGlass size={16} weight="bold" /> Cerca
            </button>
          </div>
          {lookupRes && (
            <div className="border border-zinc-200 rounded-lg p-4 space-y-2" data-testid="gc-lookup-result">
              <div className="flex items-center justify-between">
                <span className="font-mono font-bold text-lg">{lookupRes.code}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_BADGE[lookupRes.status]}`}>
                  {lookupRes.status.toUpperCase()}
                </span>
              </div>
              <div className="text-sm"><b>{lookupRes.name}</b> — {fmtEur(lookupRes.value)}</div>
              <div className="text-xs text-zinc-500">Cliente: {lookupRes.customer_name} {lookupRes.customer_phone && `· ${lookupRes.customer_phone}`}</div>
              <div className="text-xs text-zinc-500">
                Validità: {lookupRes.validita_inizio ? `dal ${fmtDate(lookupRes.validita_inizio)} ` : ""}
                {lookupRes.validita_fine ? `al ${fmtDate(lookupRes.validita_fine)}` : "senza scadenza"}
              </div>
              {lookupRes.status === "attiva" && (
                <button onClick={() => doRedeem(lookupRes.code)} disabled={!lookupRes.is_valid} data-testid="gc-lookup-redeem"
                  className="flex items-center gap-2 bg-emerald-600 text-white text-sm font-semibold px-4 py-2.5 rounded-lg hover:bg-emerald-500 disabled:opacity-40 transition-colors">
                  <CheckCircle size={16} weight="bold" /> Segna utilizzata
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {editor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditor(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4"
            onClick={(e) => e.stopPropagation()} data-testid="gc-editor">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-lg">{editor.id ? "Modifica gift card" : "Nuova gift card"}</h2>
              <button onClick={() => setEditor(null)} className="p-1.5 text-zinc-400 hover:text-zinc-700"><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-zinc-600">Nome *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="gc-form-name"
                  placeholder='Es. "Gift Card Cena" o "Buono 50€"' className="mt-1 w-full border border-zinc-300 rounded-lg px-3 py-2.5 text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-zinc-600">Valore (€) *</label>
                <input type="number" min="1" step="0.01" value={form.value} data-testid="gc-form-value"
                  onChange={(e) => setForm({ ...form, value: e.target.value })}
                  className="mt-1 w-full border border-zinc-300 rounded-lg px-3 py-2.5 text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-zinc-600">Messaggio / descrizione</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2}
                  data-testid="gc-form-description" placeholder="Testo mostrato nell'email al cliente"
                  className="mt-1 w-full border border-zinc-300 rounded-lg px-3 py-2.5 text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-zinc-600">Validità</label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  {[["date", "Date fisse"], ["giorni", "Giorni dall'assegnazione"]].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => setForm({ ...form, scadenza_mode: v })} data-testid={`gc-form-mode-${v}`}
                      className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                        form.scadenza_mode === v ? "border-cyan-500 bg-cyan-50 text-cyan-800" : "border-zinc-300 text-zinc-600"}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              {form.scadenza_mode === "date" ? (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-semibold text-zinc-600">Dal *</label>
                    <input type="date" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                      data-testid="gc-form-valid-from" className="mt-1 w-full border border-zinc-300 rounded-lg px-3 py-2.5 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-zinc-600">Al *</label>
                    <input type="date" value={form.valid_to} onChange={(e) => setForm({ ...form, valid_to: e.target.value })}
                      data-testid="gc-form-valid-to" className="mt-1 w-full border border-zinc-300 rounded-lg px-3 py-2.5 text-sm" />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="text-xs font-semibold text-zinc-600">Giorni di validità *</label>
                  <input type="number" min="1" value={form.giorni_validita} data-testid="gc-form-giorni"
                    onChange={(e) => setForm({ ...form, giorni_validita: e.target.value })}
                    className="mt-1 w-full border border-zinc-300 rounded-lg px-3 py-2.5 text-sm" />
                </div>
              )}
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  data-testid="gc-form-active" className="w-4 h-4 accent-cyan-600" />
                Gift card attiva (assegnabile ai clienti)
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditor(null)} className="px-4 py-2.5 text-sm font-medium text-zinc-600 hover:bg-zinc-100 rounded-lg">Annulla</button>
              <button onClick={save} disabled={saving} data-testid="gc-form-save"
                className="px-5 py-2.5 text-sm font-semibold bg-zinc-900 text-white rounded-lg hover:bg-zinc-700 disabled:opacity-50 transition-colors">
                {saving ? "Salvataggio..." : editor.id ? "Salva modifiche" : "Crea gift card"}
              </button>
            </div>
          </div>
        </div>
      )}

      {assignCard && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setAssignCard(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="gc-assign-dialog">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-lg">Assegna "{assignCard.name}"</h2>
              <button onClick={() => setAssignCard(null)} className="p-1.5 text-zinc-400 hover:text-zinc-700"><X size={20} /></button>
            </div>
            <div>
              <label className="text-xs font-semibold text-zinc-600">Cliente dalla rubrica</label>
              <select value={custSel} onChange={(e) => onPickCustomer(e.target.value)} data-testid="gc-assign-customer"
                className="mt-1 w-full border border-zinc-300 rounded-lg px-3 py-2.5 text-sm bg-white">
                <option value="">— Nuovo cliente / inserimento manuale —</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.first_name} {c.last_name} {c.phone ? `· ${c.phone}` : ""}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input value={custForm.first_name} onChange={(e) => setCustForm({ ...custForm, first_name: e.target.value })}
                placeholder="Nome" data-testid="gc-assign-nome" className="border border-zinc-300 rounded-lg px-3 py-2.5 text-sm" />
              <input value={custForm.last_name} onChange={(e) => setCustForm({ ...custForm, last_name: e.target.value })}
                placeholder="Cognome" data-testid="gc-assign-cognome" className="border border-zinc-300 rounded-lg px-3 py-2.5 text-sm" />
              <input value={custForm.phone} onChange={(e) => setCustForm({ ...custForm, phone: e.target.value })}
                placeholder="Telefono" data-testid="gc-assign-phone" className="border border-zinc-300 rounded-lg px-3 py-2.5 text-sm" />
              <input value={custForm.email} onChange={(e) => setCustForm({ ...custForm, email: e.target.value })}
                placeholder="Email" type="email" data-testid="gc-assign-email" className="border border-zinc-300 rounded-lg px-3 py-2.5 text-sm" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)}
                data-testid="gc-assign-send-email" className="w-4 h-4 accent-cyan-600" />
              Invia email al cliente con il codice
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setAssignCard(null)} className="px-4 py-2.5 text-sm font-medium text-zinc-600 hover:bg-zinc-100 rounded-lg">Annulla</button>
              <button onClick={doAssign} disabled={saving} data-testid="gc-assign-confirm"
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-cyan-600 text-white rounded-lg hover:bg-cyan-500 disabled:opacity-50 transition-colors">
                <PaperPlaneTilt size={16} weight="bold" /> {saving ? "Assegnazione..." : "Assegna"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
